/**
 * useDevice.ts
 *
 * Detecção de ambiente de execução. Três sinais INDEPENDENTES, porque
 * "mobile" e "PWA" são coisas diferentes e se cruzam nos 4 casos:
 * navegador no desktop, navegador no celular, PWA instalado no desktop,
 * PWA instalado no celular.
 *
 *   isMobileViewport — a tela está estreita (< 992px, mesmo corte do
 *                      Bootstrap `lg` já usado no projeto via `d-lg-none`).
 *                      É o sinal certo para ESCONDER/TROCAR LAYOUT, porque
 *                      acompanha o CSS. Desktop com janela estreita também
 *                      liga esse flag — de propósito: o layout é o mesmo.
 *
 *   isMobileDevice   — o aparelho é de fato um celular/tablet, detectado por
 *                      CAPACIDADE (ponteiro grosso + sem hover), não por
 *                      largura nem por User-Agent (que é falsificável).
 *                      Use para comportamento de INPUT (swipe, long-press,
 *                      câmera), não para layout.
 *
 *   isPwa            — está rodando instalado (standalone/fullscreen/minimal-ui
 *                      ou `navigator.standalone` no iOS < 15.4). Ortogonal aos
 *                      outros dois: PWA instalado no desktop dá isPwa = true e
 *                      isMobileDevice = false.
 *
 * Todos são reativos: `matchMedia` dispara em resize, rotação de tela,
 * split-screen, dobrável abrindo e ao instalar/abrir como app. Os listeners
 * ficam no escopo do módulo (registrados uma vez, compartilhados por todos os
 * componentes) — chamar `useDevice()` em N componentes não cria N listeners.
 *
 * Uso:
 *
 *   import { useDevice } from '@/composables/useDevice'
 *
 *   const { isMobileViewport, isPwa } = useDevice()
 *
 *   <MeuBotao v-if="!isMobileViewport" />
 */
import { readonly, ref, type Ref } from "vue";

/**
 * Corte de "mobile" = Bootstrap `lg` menos um passo (991.98px), idêntico ao que
 * as classes `d-lg-none` / `d-none d-lg-block` já aplicam no CSS. Manter os dois
 * no mesmo número evita o bug clássico de o JS esconder num ponto e o CSS noutro.
 */
const MOBILE_VIEWPORT_QUERY = "(max-width: 991.98px)";

/**
 * Ponteiro primário impreciso E sem hover = dedo em touchscreen.
 * Notebook com tela de toque continua fora (tem mouse → tem hover),
 * que é o que separa "aparelho móvel" de "aparelho com toque".
 */
const MOBILE_DEVICE_QUERY = "(pointer: coarse) and (hover: none)";

/**
 * `standalone` sozinho não basta: o app perde esse display-mode ao entrar em
 * fullscreen e o flag cairia para false no meio da sessão.
 */
const PWA_QUERY =
  "(display-mode: standalone), (display-mode: fullscreen), (display-mode: minimal-ui)";

/** Fallback só para browser sem `matchMedia`. UA é falsificável — último recurso. */
const MOBILE_UA_REGEX = /android|iphone|ipad|ipod|iemobile|blackberry|opera mini/i;

/**
 * Cria um ref amarrado a uma media query, já com o valor inicial correto.
 * Listener no escopo do módulo, sem `onUnmounted`: vive enquanto a aba viver.
 */
function useMediaQueryRef(query: string, fallback = false): Ref<boolean> {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return ref(fallback);
  }

  const mediaQueryList = window.matchMedia(query);
  const matches = ref(mediaQueryList.matches);

  const onChange = (event: MediaQueryListEvent) => {
    matches.value = event.matches;
  };

  if (typeof mediaQueryList.addEventListener === "function") {
    mediaQueryList.addEventListener("change", onChange);
  } else {
    // Safari < 14
    mediaQueryList.addListener(onChange);
  }

  return matches;
}

function detectMobileUserAgent(): boolean {
  if (typeof navigator === "undefined") return false;
  return MOBILE_UA_REGEX.test(navigator.userAgent);
}

/** iOS anterior ao Safari 15.4 não reporta `display-mode`, só essa flag. */
function detectIosStandalone(): boolean {
  if (typeof navigator === "undefined") return false;
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

const isMobileViewport = useMediaQueryRef(MOBILE_VIEWPORT_QUERY, detectMobileUserAgent());
const isMobileDevice = useMediaQueryRef(MOBILE_DEVICE_QUERY, detectMobileUserAgent());
const isPwaDisplayMode = useMediaQueryRef(PWA_QUERY, false);

const isPwa = ref(isPwaDisplayMode.value || detectIosStandalone());

// `navigator.standalone` não é reativo; o display-mode é. Espelha um no outro
// mantendo o true do iOS legado.
if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
  window.matchMedia(PWA_QUERY).addEventListener?.("change", (event) => {
    isPwa.value = event.matches || detectIosStandalone();
  });
}

export function useDevice() {
  return {
    /** Tela estreita (< 992px). Use para layout — acompanha o `d-lg-none` do CSS. */
    isMobileViewport: readonly(isMobileViewport),
    /** Celular/tablet de verdade (toque, sem hover). Use para input/gestos. */
    isMobileDevice: readonly(isMobileDevice),
    /** Rodando instalado como app. Independente de ser celular ou desktop. */
    isPwa: readonly(isPwa),
  };
}
